import React, { FC, useCallback, useMemo } from 'react'

import { Header } from 'components'
import { Page as CPage, PageContent } from 'common'
import { logBridgeError, sendSelectContent } from 'common/helpers'
import { Routes } from 'common/constants'
import { useScreenFinishLoadTac } from 'common/hooks/useScreenFinishLoadTac'
import { IRequestReportRequest, PostReportRequest } from 'common/types'
import { useLocation, useNavigate } from 'react-router-dom'
import { useRequestReportByEmail } from 'pages/your-personal-data/hooks/useRequestReportByEmail'
import { useSendReportViaEmailUS } from 'pages/your-personal-data/hooks/useSendReportViaEmailUS'
import { useAppContext } from 'contexts/app-context/hooks/useAppContext'
import { REPORT_DICTIONARY } from 'common/types/br/Response.types'
import { TReportType } from 'contexts/app-context/types'
import { Button } from 'common/styles/layouts'

import { ButtonsContainer } from './styles'
import { ReportViwer } from '../report-viewer/ReportViwer'

export const ReportLoader: FC = () => {
  const { userCountry, userLanguage, userSelectedEntity } = useAppContext()

  useScreenFinishLoadTac({
    name: 'ReportLoader',
    screen: Routes.REPORT_LOADER,
    action: 'ReportLoaderFinishLoad',
    entity: userSelectedEntity,
    shouldSend: true,
    logLabel: 'send report loader finish tac',
  })

  const navigate = useNavigate()

  const { state } = useLocation()

  const reportDataBR: IRequestReportRequest = {
    trackNumber: state?.trackNumber ?? '',
    dataSolicitacao: state?.requestDate ?? '',
    tipoSolicitacao: REPORT_DICTIONARY[state?.requestType as TReportType] ?? '',
  }

  const reportDataUS: PostReportRequest = {
    trackNumber: state?.trackNumber ?? '',
    dataRequest: state?.requestDate ?? '',
    requestType: state?.requestType ?? '',
  }

  const { sendReportFn: sendBR, isSendReportLoading: isLoadingBR } =
    useRequestReportByEmail(reportDataBR)

  const { sendReportFn: sendUS, isSendReportLoading: isLoadingUS } =
    useSendReportViaEmailUS(reportDataUS)

  const isLoading = isLoadingBR || isLoadingUS

  const onSendReportClickHandler = useCallback(async () => {
    try {
      await sendSelectContent({
        flow: 'report_loader',
        step: 'report_loader',
        screen_name: Routes.REPORT_LOADER,
        element_name: 'receiveReportByEmail',
        element_type: 'button',
        parent_name: 'ReportLoader',
        scenario: JSON.stringify({
          screen: Routes.REPORT_LOADER,
          product: userSelectedEntity,
          ref_type: 'button',
          content_action: 'touch',
        }),
      })
    } catch (error: unknown) {
      logBridgeError('send report loader email tic', error)
    }

    if (userCountry === 'US') await sendUS()
    else await sendBR()
  }, [userCountry, sendUS, sendBR, userSelectedEntity])

  const onCloseClickHandler = useCallback(async () => {
    try {
      await sendSelectContent({
        flow: 'report_loader',
        step: 'report_loader',
        screen_name: Routes.REPORT_LOADER,
        element_name: 'closeReportLoader',
        element_type: 'button',
        parent_name: 'ReportLoader',
        scenario: JSON.stringify({
          screen: Routes.REPORT_LOADER,
          product: userSelectedEntity,
          ref_type: 'button',
          content_action: 'touch',
        }),
      })
    } catch (error: unknown) {
      logBridgeError('send report loader close tic', error)
    }

    navigate(-1)
  }, [navigate, userSelectedEntity])

  const { headerTitle, sendViaEmail, cancelButtonText } = useMemo((): {
    headerTitle: string
    sendViaEmail: string
    cancelButtonText: string
  } => {
    if (userLanguage === 'en-US')
      return {
        headerTitle: 'Report',
        sendViaEmail: 'Receive by email',
        cancelButtonText: 'Close',
      }

    if (userLanguage === 'es-ES')
      return {
        headerTitle: 'Informe',
        sendViaEmail: 'Recibir por correo electrónico',
        cancelButtonText: 'Cerrar',
      }

    return {
      headerTitle: 'Relatório',
      sendViaEmail: 'Receber por e-mail',
      cancelButtonText: 'Fechar',
    }
  }, [userLanguage])

  return (
    <CPage>
      <Header title={headerTitle} />
      <PageContent>
        <ReportViwer />
        <ButtonsContainer>
          {userCountry === 'BR' && (
            <Button
              variant="primary"
              isLoading={isLoading}
              onClick={onSendReportClickHandler}
              className="nohover"
            >
              {sendViaEmail}
            </Button>
          )}
          <Button variant="secondary" onClick={onCloseClickHandler}>
            {cancelButtonText}
          </Button>
        </ButtonsContainer>
      </PageContent>
    </CPage>
  )
}
